/**
 * Placing a new widget: what the picker chose (its type and any data it was
 * picked for, e.g. one metric of one source) filled out with sensible
 * defaults, at the first free spot or directly below the widget whose "+"
 * was used. Below an existing widget, everything under it moves down.
 */

import { DEFAULT_SIZE, firstFreeSpot, fitsPage, settle } from './grid.ts';
import type { Catalogue, Widget, WidgetData, WidgetDisplay, WidgetType } from './reports-api.ts';
import { NEEDS_DIMENSION } from './VisualPicker.tsx';

export interface WidgetSeed {
  type: WidgetType;
  data?: WidgetData;
  display?: WidgetDisplay;
}

function filledData(seed: WidgetSeed, catalogue: Catalogue): WidgetData {
  if (!catalogue.data_widget_types.includes(seed.type)) return {};
  const source =
    catalogue.sources.find((item) => item.key === seed.data?.source) ?? catalogue.sources[0];
  if (!source) return {};
  const metrics =
    seed.data?.metrics ??
    source.metrics.slice(0, seed.type === 'STACKED_BAR' ? 2 : 1).map((metric) => metric.key);
  const dimension =
    seed.data?.dimension ??
    (NEEDS_DIMENSION.includes(seed.type)
      ? (source.dimensions.find((item) => item.key !== 'date')?.key ?? null)
      : null);
  return { ...seed.data, source: source.key, metrics, dimension };
}

function defaultDisplay(type: WidgetType): WidgetDisplay {
  if (type === 'HEADER') return { title: 'New section' };
  if (type === 'TOC') return { title: 'Contents' };
  return {};
}

/** The new widgets for the page, or null when the page has no room for it. */
export function placeWidget(
  seed: WidgetSeed,
  catalogue: Catalogue,
  widgets: Widget[],
  options: { maxRows: number | null; below?: Widget | undefined; autoAlign: boolean },
): { widgets: Widget[]; index: number } | null {
  const size = DEFAULT_SIZE[seed.type];
  const widget: Widget = {
    id: crypto.randomUUID(),
    type: seed.type,
    x: 0,
    y: 0,
    ...size,
    data: filledData(seed, catalogue),
    display: { ...defaultDisplay(seed.type), ...seed.display },
  };
  if (options.below) {
    const x = Math.min(options.below.x, 12 - size.w);
    const next = settle(
      [...widgets, { ...widget, x, y: options.below.y + options.below.h }],
      widgets.length,
      options.autoAlign,
    );
    return fitsPage(next, options.maxRows) ? { widgets: next, index: widgets.length } : null;
  }
  const spot = firstFreeSpot(widgets, size, options.maxRows);
  if (!spot) return null;
  return { widgets: [...widgets, { ...widget, ...spot }], index: widgets.length };
}
